
/**
 * Examples of how to use the monitoring service
 * This file is for demonstration purposes only
 */

import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  initializeMonitoring,
  captureError,
  captureMessage,
  capturePerformance,
  setMonitoringUser,
  monitoringService,
  useErrorTracking
} from './monitoringService';
import { logInfo, trackPerformance } from './logger';
import ErrorBoundary from '@/components/ErrorBoundary';

// Example of initializing monitoring in a React app
export const initializeAppMonitoring = async () => {
  // Initialize with multiple services
  await initializeMonitoring(['sentry', 'logrocket'], {
    sentry: {
      dsn: 'https://exampledsn@sentry.io/example',
      environment: process.env.NODE_ENV
    },
    logrocket: {
      appId: 'example/app'
    }
  });

  logInfo('Application monitoring initialized', 'app');
};

// Hook to set the current user in monitoring tools when auth state changes
export const useMonitoringUser = () => {
  const { profile } = useAuth();
  
  useEffect(() => {
    if (profile) {
      // Set user identity in monitoring tools
      setMonitoringUser({
        id: profile.id,
        email: profile.email,
        role: profile.role
      });
      
      captureMessage('User logged in', 'info', { userId: profile.id });
    }
  }, [profile]);
};

// Example of tracking a React component's render performance
export const useRenderMonitoring = (componentName: string) => {
  useEffect(() => {
    const startTime = performance.now();
    
    return () => {
      const duration = performance.now() - startTime;
      const perfTracker = capturePerformance(`${componentName}_render`, duration, {
        component: componentName
      });
      
      // Optionally update with additional context later
      perfTracker({
        renderCompleted: true,
        timestamp: new Date().toISOString()
      });
    };
  }, [componentName]);
};

// Example function showing how to use error tracking
export const demonstrateErrorCapturing = () => {
  try {
    // Simulate an error
    throw new Error('Example monitored error');
  } catch (error) {
    // Capture the error in monitoring tools
    if (error instanceof Error) {
      captureError(error, {
        location: 'demonstrateErrorCapturing',
        timestamp: new Date().toISOString()
      });
    }
  }
};

// Example of manually timing an expensive operation
export const trackExpensiveOperation = async (
  operationName: string, 
  operation: () => Promise<any>
) => {
  const startTime = performance.now();
  const perfTracker = capturePerformance(operationName, 0, {
    started: true,
    timestamp: new Date().toISOString()
  });
  
  try {
    // Execute the operation
    const result = await operation();
    
    // Track the performance
    const duration = performance.now() - startTime;
    perfTracker({
      success: true,
      durationMs: duration,
      resultSize: JSON.stringify(result).length
    });
    
    return result;
  } catch (error) {
    // Track both the error and performance
    const duration = performance.now() - startTime;
    
    perfTracker({
      success: false,
      durationMs: duration,
      errorType: error instanceof Error ? error.constructor.name : typeof error
    });
    
    if (error instanceof Error) {
      captureError(error, {
        operation: operationName,
        durationMs: duration
      });
    }
    
    throw error;
  }
};

// Example of integrating with monitoring during app initialization
export const setupAppMonitoring = () => {
  // Log navigation timing metrics
  if (performance && 'getEntriesByType' in performance) {
    try {
      const navigationEntries = performance.getEntriesByType('navigation');
      if (navigationEntries.length > 0) {
        // Cast to PerformanceNavigationTiming which has the specific properties we need
        const navigationTiming = navigationEntries[0] as PerformanceNavigationTiming;
        
        capturePerformance('page_load', navigationTiming.duration, {
          domInteractive: navigationTiming.domInteractive,
          domComplete: navigationTiming.domComplete,
          loadEvent: navigationTiming.loadEventEnd
        });
      }
    } catch (e) {
      // Ignore any performance API errors
    }
  }
  
  logInfo('App monitoring setup complete', 'app');
};

// Example of how to use ErrorBoundary component in a React app
export const ErrorBoundaryExample = ({ children }: { children: React.ReactNode }) => {
  return (
    <ErrorBoundary 
      component="ExampleComponent" 
      onReset={() => {
        // Do something when user clicks the retry button
        captureMessage('User clicked retry after error', 'info');
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

// Example of using the useErrorTracking hook
export const ErrorTrackingExample = ({ children }: { children: React.ReactNode }) => {
  const { hasError, handleError } = useErrorTracking('ExampleComponent');
  
  // Example of how you could use the error state
  if (hasError) {
    return (
      <div className="error-state">
        Something went wrong. Please try again.
      </div>
    );
  }
  
  // Wrap children in an error handler
  return (
    <div>
      {(() => {
        try {
          return children;
        } catch (error) {
          if (error instanceof Error) {
            handleError(error, { componentStack: '' } as React.ErrorInfo);
          }
          return <div>Error occurred</div>;
        }
      })()}
    </div>
  );
};

// Example of creating a custom error tracking wrapper
export const withErrorTracking = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName: string
) => {
  return function WithErrorTracking(props: P) {
    return (
      <ErrorBoundary component={componentName}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
};
